// The CountdownTimerBanner component displays a dynamic promotional banner with a real-time countdown timer
// until a specified end date. It supports localization (e.g., en-GB, en-EU) and adapts its layout, styling,
// and visibility for mobile and desktop devices. The banner can include a customizable message, timer labels,
//  and an optional image. Once the countdown expires, it switches to a fallback message or design.
//  It also supports configurable backgrounds, spacing, and typography styles, providing flexibility for
//  marketing or campaign-driven displays.

import React, { useEffect, useState } from 'react';
import { CountdownTimerBannerType } from 'types/next';
import useLocale from 'helpers/hooks/useLocale';
import ReactMarkdown from 'react-markdown';
import Link from 'components/commercetools-ui/atoms/link';

interface CountdownTimerBannerProps {
  data: CountdownTimerBannerType;
  isMobileDevice: boolean;
}

const CountdownTimerBanner = ({ data, isMobileDevice }: CountdownTimerBannerProps) => {
  const { dataSource: content } = data?.data || {};
  const {
    countdownMessage,
    endDateTime,
    digitLabels,
    image,
    backgroundGradient,
    backgroundColor,
    gap,
    link,
    fallbackContent,
    padding,
  } = content || {};
  const { getCurrency } = useLocale();
  const selectedLocale = getCurrency() === 'GBP' ? 'en-GB' : 'en-EU';
  const endTime = new Date(endDateTime?.[selectedLocale]);
  const now = new Date();

  const [remaining, setRemaining] = useState(() => endTime?.getTime() - now?.getTime());
  const [isExpired, setIsExpired] = useState(remaining <= 0);

  useEffect(() => {
    if (remaining <= 0) return;
    const interval = setInterval(() => {
      const diff = endTime?.getTime() - new Date().getTime();
      if (diff <= 0) {
        clearInterval(interval);
        setIsExpired(true);
        setRemaining(0);
      } else {
        setRemaining(diff);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [endTime]);

  const formatTime = (ms: number) => {
    const totalSecs = Math.floor(ms / 1000);
    const days = Math.floor(totalSecs / (3600 * 24));
    const hours = Math.floor((totalSecs % (3600 * 24)) / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    return { days, hours, mins, secs };
  };

  const { days, hours, mins, secs } = formatTime(remaining);

  const getFont = (style: any, keyDesktop: string, keyMobile: string) =>
    isMobileDevice ? style?.[keyMobile] : style?.[keyDesktop];

  if (isExpired) {
    const fallbackMessage = fallbackContent?.[selectedLocale] || 'Deal expired';
    return (
      <section
        data-qa="countdown-banner-fallback"
        className="flex items-center justify-center leading-none"
        style={{
          background: fallbackContent?.backgroundGradient || fallbackContent?.backgroundColor || 'transparent',
          gap: isMobileDevice ? gap?.mobile : gap?.desktop,
          padding: isMobileDevice ? padding?.mobile : padding?.desktop,
        }}
      >
        <span
          data-qa="countdown-fallback"
          className="text-center leading-none"
          style={{
            color: fallbackContent?.fallBackMessageStyle?.textColor,
            fontSize: getFont(fallbackContent?.fallBackMessageStyle, 'fontSizeDesktopPx', 'fontSizeMobilePx'),
          }}
        >
          <ReactMarkdown
            components={{
              strong: ({ node, ...props }) => (
                <strong
                  style={{
                    fontWeight: fallbackContent?.fallBackMessageStyle?.fontWeight,
                  }}
                >
                  {props.children}
                </strong>
              ),
            }}
          >
            {fallbackMessage}
          </ReactMarkdown>
        </span>
      </section>
    );
  }

  const messageText = isMobileDevice
    ? countdownMessage?.isMobile?.[selectedLocale]
    : countdownMessage?.isDesktop?.[selectedLocale];

  const showMessage =
    (isMobileDevice && countdownMessage?.visiblityMobile) || (!isMobileDevice && countdownMessage?.visiblityDesktop);

  const showTimer =
    (isMobileDevice && endDateTime?.visiblityMobile) || (!isMobileDevice && endDateTime?.visiblityDesktop);

  const showDigitLabels =
    (isMobileDevice && digitLabels?.visiblityMobile) || (!isMobileDevice && digitLabels?.visiblityDesktop);

  const showImage = (isMobileDevice && image?.visiblityMobile) || (!isMobileDevice && image?.visiblityDesktop);

  const imageSet = isMobileDevice ? image?.isMobile?.[selectedLocale] : image?.isDesktop?.[selectedLocale];

  const timerStyle = {
    color: endDateTime?.textColor,
    fontSize: getFont(endDateTime, 'fontSizeDesktopPx', 'fontSizeMobilePx'),
    fontWeight: getFont(endDateTime, 'fontWeightDesktop', 'fontWeightMobile'),
    lineHeight: 1,
  };

  const labelStyle = {
    color: digitLabels?.textColor,
    fontSize: getFont(digitLabels, 'fontSizeDesktopPx', 'fontSizeMobilePx'),
    fontWeight: getFont(digitLabels, 'fontWeightDesktop', 'fontWeightMobile'),
    lineHeight: 1,
  };

  return (
    <Link link={`${link?.[selectedLocale] || ''}`} className="w-full">
      <section
        data-qa="countdown-banner"
        className="flex items-center justify-center leading-none"
        style={{
          background: backgroundGradient || backgroundColor || 'transparent',
          gap: isMobileDevice ? gap?.mobile : gap?.desktop,
          padding: isMobileDevice ? padding?.mobile : padding?.desktop,
        }}
      >
        <div
          className="flex flex-wrap items-center justify-center leading-none"
          style={{
            flexDirection:
              countdownMessage?.countdownMessagePos === 'right' ? ('row-reverse' as const) : ('row' as const),
            gap: isMobileDevice ? gap?.mobile : gap?.desktop,
          }}
        >
          {showMessage && (
            <span
              data-qa="countdown-message"
              className="text-center leading-none"
              style={{
                color: countdownMessage?.countDownMessageStyle?.textColor,
                fontSize: getFont(countdownMessage?.countDownMessageStyle, 'fontSizeDesktopPx', 'fontSizeMobilePx'),
              }}
            >
              <ReactMarkdown
                components={{
                  strong: ({ node, ...props }) => (
                    <strong
                      style={{
                        fontWeight: countdownMessage?.countDownMessageStyle?.fontWeight,
                      }}
                    >
                      {props.children}
                    </strong>
                  ),
                }}
              >
                {messageText}
              </ReactMarkdown>
            </span>
          )}

          {showTimer && (
            <div
              data-qa="countdown-timer"
              className="flex items-center leading-none"
              style={{
                gap: digitLabels?.gap,
              }}
            >
              {[
                { value: days, label: digitLabels?.days },
                { value: hours, label: digitLabels?.hours },
                { value: mins, label: digitLabels?.mins },
                { value: secs, label: digitLabels?.secs },
              ].map((unit, idx) => (
                <div
                  key={idx}
                  className="flex items-start justify-center"
                  style={{
                    lineHeight: 1,
                    gap: digitLabels?.gap,
                  }}
                >
                  {' '}
                  <div
                    className="flex flex-col items-center justify-center leading-none"
                    style={{ gap: digitLabels?.gap }}
                  >
                    <span style={timerStyle} data-qa={`countdown-${unit?.label?.toLowerCase()}`}>
                      {unit?.value?.toString().padStart(2, '0')}
                    </span>
                    {showDigitLabels && (
                      <span
                        style={labelStyle}
                        data-qa={`countdown-${unit?.label?.toLowerCase()}-label`}
                        className="min-w-[36px] text-center md:w-[40px]"
                      >
                        {unit?.label}
                      </span>
                    )}
                  </div>
                  {idx < 3 && <span style={timerStyle}>:</span>}
                </div>
              ))}
            </div>
          )}
        </div>
        {showImage && imageSet && (
          <img
            data-qa="countdown-image"
            src={`https://${imageSet?.defaultHost}/i/${imageSet?.endpoint}/${imageSet?.name}`}
            alt={imageSet?.name}
            style={{
              maxWidth: isMobileDevice ? image?.width?.mobile ?? '45px' : image?.width?.desktop ?? '45px',
              height: 'auto',
            }}
          />
        )}
      </section>
    </Link>
  );
};

export default CountdownTimerBanner;
